import { useCopilotChatInternal as useCopilotChat } from '@copilotkit/react-core';
import { MessagesProps, useChatContext } from '@copilotkit/react-ui';
import { isEmpty } from 'lodash';
import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import BuglyCopilotIcon from '@/assets/images/header/bugly-copilot-icon.svg?react';
import { useAgentContext } from './agent';
import { agentService } from './agent-service';
import { MessageControlBar } from './control-bar';
import style from './index.module.scss';

/**
 * 将消息列表按"请求-响应"进行分组合并
 * 从一个 user 消息到下一个 user 消息之间的所有 assistant/tool 消息作为子消息
 * tool 消息的调用和结果合并成一条
 * 返回树形结构：每个消息可能包含 childMessages 数组
 */
function mergeMessages(messages: any[]): any[] {
  const merged: any[] = [];
  let currentGroup: any[] = [];

  for (const msg of messages) {
    if (msg.role === 'user') {
      // 遇到 user 消息，结束上一组
      if (currentGroup.length > 0) {
        // 处理当前组内的 tool 消息合并
        const processedGroup = mergeToolMessages(currentGroup);
        const firstMsg = processedGroup[0];

        // 将上一组的消息合并为一条，包含子消息
        const groupedMessage = {
          ...firstMsg, // 使用第一条消息作为父消息
          childMessages: processedGroup, // 将所有消息作为子消息
          id: firstMsg.id,
        };
        merged.push(groupedMessage);
        currentGroup = [];
      }
      // 添加 user 消息（user 消息不需要合并）
      merged.push(msg);
    } else {
      currentGroup.push(msg);
    }
  }

  // 处理最后一组
  if (currentGroup.length > 0) {
    const processedGroup = mergeToolMessages(currentGroup);
    const firstMsg = processedGroup[0];
    const groupedMessage = {
      ...firstMsg,
      childMessages: processedGroup,
      id: firstMsg.id,
    };
    merged.push(groupedMessage);
  }
  return merged;
}

/**
 * 合并 tool 消息：将 toolCall 和对应的 tool 结果合并成一条消息
 * 同一 toolCallId 的多条流式 ResultMessage 按 delta 累加拼接，
 * 含 toolCalls 的 assistant 消息若有文本内容也会保留输出
 */
function mergeToolMessages(messages: any[]): any[] {
  const result: any[] = [];
  const toolCallMap = new Map<string, any>();
  const mergedToolMap = new Map<string, any>();
  const pushedAssistantIds = new Set<string>();

  for (const msg of messages) {
    if (msg.role === 'assistant' && msg.toolCalls) {
      msg.toolCalls.forEach((call: any) => {
        toolCallMap.set(call.id, { callMessage: msg, call });
      });
      // assistant 消息自身带有文本内容时，保留文本部分单独输出
      if (msg.content) {
        result.push({ ...msg, toolCalls: undefined });
        pushedAssistantIds.add(msg.id);
      }
    } else if (msg.role === 'tool') {
      const { toolCallId } = msg;
      const existingMerged = toolCallId ? mergedToolMap.get(toolCallId) : null;

      if (existingMerged) {
        // 流式 delta：累加拼接而非覆盖
        existingMerged.isStreaming = true;
        if (msg.content) {
          existingMerged.toolResult += msg.content;
        }
      } else {
        const toolCallInfo = toolCallId ? toolCallMap.get(toolCallId) : null;
        const mergedToolMsg = {
          ...msg,
          id: `tool-merged-${toolCallId || msg.id}`,
          role: 'tool',
          toolName: msg.toolName,
          toolCallId,
          toolCall: toolCallInfo?.call,
          toolResult: msg.content || '',
          isMergedTool: true,
        };
        result.push(mergedToolMsg);

        if (toolCallId) {
          mergedToolMap.set(toolCallId, mergedToolMsg);
          toolCallMap.delete(toolCallId);
        }
      }
    } else {
      result.push(msg);
    }
  }

  // 未匹配的 toolCall：仅在 assistant 消息尚未被输出时补入
  toolCallMap.forEach((toolCallInfo) => {
    if (!pushedAssistantIds.has(toolCallInfo.callMessage.id)) {
      result.push(toolCallInfo.callMessage);
    }
  });

  return result;
}

export const CustomCopilotMessages = ({
  inProgress,
  children,
  RenderMessage,
  AssistantMessage,
  UserMessage,
  ErrorMessage,
  ImageRenderer,
  onRegenerate,
  onCopy,
  onThumbsUp,
  onThumbsDown,
  markdownTagRenderers,
  chatError,
}: MessagesProps) => {
  const { t } = useTranslation();
  const { labels } = useChatContext();
  const { messages: visibleMessages, interrupt } = useCopilotChat();

  const initialMessages = useMemo(() => makeInitialMessages(labels.initial), [labels.initial]);
  const messages = [...initialMessages, ...visibleMessages];
  const { messagesContainerRef, messagesEndRef } = useScrollToBottom(messages);
  const { currentAgent, agentOptions, setQuickText } = useAgentContext(); // 从 context 获取 agentOptions

  // Determine which render component to use
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const MessageRenderer = RenderMessage;

  const quickStart = (message: string) => {
    setQuickText(message);
  };

  const agentMessages = useMemo(() => {
    const newMessages = messages.map((msg) => {
      if (agentService.has(msg.id)) {
        const agent = agentService.get(msg.id);
        return {
          ...msg,
          agentId: agent.agentId,
          agentName: agent.agentName,
        };
      }

      const msgAgent = agentOptions.find(opt => opt.value === `${currentAgent}`);

      agentService.set(msg.id, {
        agentId: msgAgent?.value ?? '',
        agentName: msgAgent?.label ?? '',
      });

      return {
        ...msg,
        agentId: msgAgent?.value ?? '',
        agentName: msgAgent?.label ?? '',
      };
    });

    // setAgentMap(newMap);
    return newMessages;
  }, [messages]);

  // 基于处理后的消息进行合并
  const mergedMessages = useMemo(() => mergeMessages(agentMessages), [agentMessages]);

  useEffect(() => {
    window.__MESSAGE_AGENT_MAP = new Map();
  }, []);

  if (isEmpty(messages)) {
    return (
      <div className={style.emptyStateOverlay}>
        <div className={style.emptyStateContent}>
          <BuglyCopilotIcon className={style.emptyStateIcon} />
          <h2 className={style.emptyStateTitle}>
          {t('👋 欢迎使用Bugly Copilot')}
          </h2>
          <p className={style.emptyStateDescription}>
            {t('我可以帮助您分析崩溃数据、查询趋势变化、对比版本差异等。')}<br />
            {t('请在下方输入您的问题开始对话。')}
          </p>
          <div className={style.emptyStateExamples}>
            {[
              t('近7天发布了哪些版本？'),
              t('今天的应用崩溃率是多少？'),
              t('最近一周有没有突然劣化的异常？'),
              t('应用最新版本是否有劣化和新增的问题？'),
              t('生成本周应用的质量报告。'),
            ].map(example => (
              <div
                key={example}
                className={style.emptyStateExampleCard}
                onClick={() => quickStart(example)}
              >
                {example}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="copilotKitMessages" ref={messagesContainerRef}>
      <div className="copilotKitMessagesContainer">
        {isEmpty(messages) && (
          <div className={style.emptyStateOverlay}>
            <div className={style.emptyStateContent}>
              <BuglyCopilotIcon className={style.emptyStateIcon} />
              <h2 className={style.emptyStateTitle}>
              {t('👋 欢迎使用Bugly Copilot')}
              </h2>
              <p className={style.emptyStateDescription}>
                {t('我可以帮助您分析崩溃数据、查询趋势变化、对比版本差异等。')}<br />
                {t('请在下方输入您的问题开始对话。')}
              </p>
              <div className={style.emptyStateExamples}>
                {[
                  t('近7天发布了哪些版本？'),
                  t('今天的应用崩溃率是多少？'),
                  t('最近一周有没有突然劣化的异常？'),
                  t('应用最新版本是否有劣化合新增的问题？'),
                  t('生成本周应用的质量报告。'),
                ].map(example => (
                  <div
                    key={example}
                    className={style.emptyStateExampleCard}
                    onClick={() => quickStart(example)}
                  >
                    {example}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {mergedMessages.map((mergedMsg, index) => {
          const isLastMessage = index === mergedMessages.length - 1;
          // 判断是否是最后一条 assistant 消息
          const isLastAssistantMessage = mergedMsg.role === 'assistant' && (() => {
            for (let i = index + 1; i < mergedMessages.length; i++) {
              if (mergedMessages[i].role === 'assistant') {
                return false;
              }
            }
            return true;
          })();
          const isCurrentMessage = inProgress && isLastMessage;

          if (
            mergedMsg?.childMessages?.length === 1
            && !mergedMsg.childMessages?.[0]?.content
            && !isCurrentMessage
          ) {
            return null;
          }

          if (mergedMsg.childMessages) {
            return (
              <div key={mergedMsg.id} className="copilotKitMessage copilotKitMessageGroup">
                {mergedMsg?.agentId && <div
                  className="copilotKitMessageGroupHeader"
                >
                  {(() => {
                    const currentAgentOption = agentOptions.find(opt => opt.value === mergedMsg.agentId);
                    return (
                      <span className='copilotKitMessageGroupHeaderIcon'>
                        {currentAgentOption?.icon || agentOptions?.[0]?.icon}
                      </span>
                    );
                  })()}
                  <span>{mergedMsg?.agentName}</span>
                </div>}
                <div className="copilotKitMessageGroupContent">
                  {mergedMsg.childMessages.map((childMsg: any, childIndex: number) => {
                    const isLastChild = childIndex === mergedMsg.childMessages.length - 1;
                    const isCurrentChild = isCurrentMessage && isLastChild;

                    return (
                      <MessageRenderer
                        key={childIndex}
                        message={childMsg}
                        inProgress={isCurrentChild ? inProgress : false}
                        index={childIndex}
                        isCurrentMessage={isCurrentChild}
                        AssistantMessage={AssistantMessage}
                        UserMessage={UserMessage}
                        ImageRenderer={ImageRenderer}
                        onRegenerate={onRegenerate}
                        onCopy={onCopy}
                        onThumbsUp={onThumbsUp}
                        onThumbsDown={onThumbsDown}
                        markdownTagRenderers={markdownTagRenderers}
                      />
                    );
                  })}
                </div>
                {mergedMsg.role === 'assistant' && <MessageControlBar
                  isCurrentMessage={isCurrentMessage}
                  isLastAssistantMessage={isLastAssistantMessage}
                  inProgress={inProgress}
                  onRegenerate={onRegenerate}
                  onCopy={onCopy}
                  onThumbsUp={onThumbsUp}
                  onThumbsDown={onThumbsDown}
                  message={mergedMsg}
                />}
              </div>
            );
          }

          return (
            <Fragment>
              <MessageRenderer
                key={index}
                message={mergedMsg}
                inProgress={inProgress}
                index={index}
                isCurrentMessage={isCurrentMessage}
                AssistantMessage={AssistantMessage}
                UserMessage={UserMessage}
                ImageRenderer={ImageRenderer}
                onRegenerate={onRegenerate}
                onCopy={onCopy}
                onThumbsUp={onThumbsUp}
                onThumbsDown={onThumbsDown}
                markdownTagRenderers={markdownTagRenderers}
              />
              {mergedMsg.role === 'assistant' && <MessageControlBar
                isCurrentMessage={isCurrentMessage}
                isLastAssistantMessage={isLastAssistantMessage}
                inProgress={inProgress}
                onRegenerate={onRegenerate}
                onCopy={onCopy}
                onThumbsUp={onThumbsUp}
                onThumbsDown={onThumbsDown}
                message={mergedMsg}
              />}
            </Fragment>
          );
        })}
        {interrupt}
        {chatError && ErrorMessage && <ErrorMessage error={chatError} isCurrentMessage />}
      </div>
      <footer className="copilotKitMessagesFooter" ref={messagesEndRef}>
        {children}
      </footer>
    </div>
  );
};

function makeInitialMessages(initial: string | string[] | undefined): any[] {
  if (!initial) return [];

  if (Array.isArray(initial)) {
    return initial.map(message => ({
      id: message,
      role: 'assistant',
      content: message,
    }));
  }

  return [
    {
      id: initial,
      role: 'assistant',
      content: initial,
    },
  ];
}

export function useScrollToBottom(messages: any[]) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const isUserScrollUpRef = useRef(false);

  const scrollToBottom = () => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      isProgrammaticScrollRef.current = true;
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleScroll = () => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false;
      return;
    }

    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      isUserScrollUpRef.current = scrollTop + clientHeight < scrollHeight;
    }
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    const mutationObserver = new MutationObserver(() => {
      if (!isUserScrollUpRef.current) {
        scrollToBottom();
      }
    });

    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    isUserScrollUpRef.current = false;
    scrollToBottom();
  }, [messages.filter(m => m.role === 'user').length]);

  return { messagesEndRef, messagesContainerRef };
}
